import { Client, SubscribePayload, createClient } from "graphql-ws";
import { useMemo } from "react";
import { useSubscription } from "react-query-subscription";
import { Observable } from "rxjs";

import { OrderDto, ViewerDto } from "graphql/generated";
import graphQLClient from "graphql/react-query";
import { config } from "src/config";

export const getOrderDetailQuery = `query planDetailData($input: V2OrderFilterInput) {
  viewer {
    user {
      patient {
        orders(inputOrder: $input) {
          sysId
          status
          stripeSubscriptionId
          planStatus
          orderPriceId
          orderPrescriptionPriceId
          isSubscriptionPaused
          isEDTrial
          delivery
          isAutoRenewal
          evaluationId
          paymentIntentUrl
          paymentProvider
          deliveryMethod
          pickupLocation {
            type
          }
          deliveryAddress {
            address
          }
          consultations {
            sysId
            videoChannelId
            type
            progressStatus
          }
          price {
            id
            type
            metadata {
              strength
              frequency
              quantity
              dosage
            }
            productInfo {
              id
              name
              uniqName
              image {
                noahVertical
                noahWide
                zoeyVertical
                zoeyWide
              }
              texts {
                product_name
                strength
              }
              productInstructions {
                localeKey
                translatedText
              }
            }
            texts {
              frequency_quantity_display_patient
              price_frequency
              actual_price_display
              dosage_display
              frequency_display
            }
          }
          oldPriceInfo {
            id
            metadata {
              frequency
              quantity
              dosage
            }
            texts {
              frequency_quantity_display_patient
              price_frequency
              actual_price_display
            }
          }
          acuityAppointment {
            sysId
            calendar
            datetime
            doctor {
              email
              fullName
            }
          }
          upcomingInvoice {
            next_payment_attempt
            period_end
            amount_due
            lines {
              data {
                amount
                discount
                plan {
                  metadata
                }
              }
            }
          }
          prescribedAfterTrialOrder {
            sysId
          }
          continueTrial
          evaluationId
          subscription {
            status
            latestInvoice {
              status
              paymentStatus
              hosted_invoice_url
            }
          }
        }
        cart {
          sysId
          status
          cartItems {
            price {
              active
              id
              unit_amount_decimal
            }
            consultationSessionType
            product {
              active
              uniqName
              id
              evaluationId
              metadata
              image {
                noahVertical
                noahWide
              }
            }
          }
        }
        
        latestPaymentMethod {
          card {
            brand
            last4
            exp_month
            exp_year
          }
        }
      }
    }
  }
}
`;

const subcribeOrderQuery = `subscription sOrder($input: V2OrderFilterInput!) {
  subscribeOrder(input: $input) {
    sysId
  }
}`;

/**
 * @see https://github.com/enisdenjo/graphql-ws#observable
 */
function fromWsClientSubscription<TData = Record<string, OrderDto>>(
  client: Client,
  payload: SubscribePayload
) {
  return new Observable<OrderDto | null>((observer) =>
    client.subscribe<TData>(payload, {
      next: async (data: any) => {
        if (data.data?.subscribeOrder?.sysId) {
          const res = await graphQLClient.request<{ viewer: ViewerDto }>(getOrderDetailQuery, {
            input: {
              sysId: data.data?.subscribeOrder?.sysId
            }
          });
          observer.next(res?.viewer?.user?.patient?.orders?.[0] || null);
        }
      },
      error: (err) => observer.error(err),
      complete: () => observer.complete()
    })
  );
}

export const useSubscribeOrder = (orderId: string) => {
  const client = useMemo(() => {
    return createClient({ url: config.WS_URI, retryAttempts: 8 });
  }, []);

  const { data, isLoading } = useSubscription(
    `order-detail-${orderId}`,
    () =>
      fromWsClientSubscription(client, {
        query: subcribeOrderQuery,
        variables: {
          input: {
            sysId: orderId
          }
        }
      }),
    {
      // options
    }
  );

  if (!isLoading) {
    return data || null;
  }

  return null;
};
